import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import Link from '@mui/material/Link';
import AddIcon from '@mui/icons-material/Add';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import LabelIcon from '@mui/icons-material/Label';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase';
import { useCompanyUsers } from '../../hooks/useCompanyUsers';
import { useCompanyAiAgents } from '../../hooks/useCompanyAiAgents';
import { usePermissions } from '../../hooks/usePermissions';
import { useDisplayTimeZone } from '../../hooks/useDisplayTimeZone';
import { statusLabel, priorityLabel } from '../../constants/tickets';
import { appendTicketLog, participantsForTicket } from '../../lib/ticketLogs';
import { appendTicketInboxAlert } from '../../lib/ticketAlerts';
import { fetchLinkedTaskSummary } from '../../lib/taskTicketLinks';
import { DASHBOARD_TAB_PATHS, dashboardCodeReviewPath } from '../../lib/dashboardRoutes';
import { codeSandboxesCollection } from '../../lib/codeSandboxReview';
import { useFirestoreQuery } from '../../hooks/useFirestoreQuery';
import { useAgentApps } from '../../hooks/useAgentApps';
import { useWorkspace } from '../../context/WorkspaceContext';
import { buildCompanyTicketsQuery } from '../../lib/ticketQueries';
import {
  buildTicketTargetRepoPayload,
  buildLinkedReadReposPayload,
  parseTargetRepoSelectKey,
  resolveTargetRepoSelectKey,
  resolveLinkedReadRepoSelectKeys,
  PROJECT_KIND_GAME,
} from '../../lib/ticketTargetRepo';
import TicketBulkActions from './TicketsHub/TicketBulkActions';
import TicketTable from './TicketsHub/TicketTable';
import TicketDetailModal from './TicketsHub/TicketDetailModal';
import TicketDetailView from './TicketsHub/TicketDetailView';
import {
  AGENT_LIKE,
  LABEL_MANAGERS,
  canEditTicket,
  canDeleteTicket,
  firestoreIndexStillBuilding,
  firestoreIndexUrlFromError,
} from './TicketsHub/ticketHubHelpers';
import {
  ASSIGNEE_KIND_AI,
  agentToAssigneeOption,
  assigneeOptionKey,
  buildAssigneeAutocompleteOptions,
  buildClearAiAssignmentIfTerminal,
  buildTicketAssignmentPayload,
  humanToAssigneeOption,
  resolveAssigneeOptionFromTicket,
  ticketAssigneeDisplayLabel,
  ticketAssigneeKey,
} from '../../lib/ticketAssignees';
import { buildTicketBodyPayload } from '../../lib/richText';

export default function TicketsHub({ userData, openTicketId, onConsumedOpenTicket, newTicketDraft, onConsumedNewTicketDraft }) {
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = userData?.companyId;
  const role = userData?.role;
  const uid = auth.currentUser?.uid;
  const { workspace } = useWorkspace();
  const forceTicketAppSelection = workspace?.forceTicketAppSelection === true;
  const email = auth.currentUser?.email || '';
  const { checkPermission } = usePermissions(userData);
  const { formatDateTime, timeZoneLabel } = useDisplayTimeZone(userData);
  const isPlatformSuperAdmin = role === 'super_admin';
  const canWriteTicketing = isPlatformSuperAdmin || checkPermission('ticketing', 'write');

  const canModifyTicket = (ticket) =>
    canWriteTicketing && canEditTicket(role, uid, ticket);

  const canRemoveTicket = (ticket) =>
    canWriteTicketing && canDeleteTicket(role, uid, ticket);

  const canManageLabels =
    isPlatformSuperAdmin || (canWriteTicketing && LABEL_MANAGERS.includes(role));

  const [tab, setTab] = useState('active');
  const [error, setError] = useState('');
  const [superAdminUsers, setSuperAdminUsers] = useState([]);
  const [ticketIndexWaiting, setTicketIndexWaiting] = useState(false);
  const [ticketIndexConsoleUrl, setTicketIndexConsoleUrl] = useState(null);
  const [ticketsRetryKey, setTicketsRetryKey] = useState(0);
  const [showAiActivityOnly, setShowAiActivityOnly] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [descriptionEditing, setDescriptionEditing] = useState(false);
  const [activePeekTicketId, setActivePeekTicketId] = useState(null);
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState(null);
  const [openWorkspaceTickets, setOpenWorkspaceTickets] = useState([]);
  const [isTicketMaximized, setIsTicketMaximized] = useState(false);

  const archivedView = tab === 'archive';

  const ticketsQuery = useMemo(() => {
    if (!companyId || !uid || (tab !== 'active' && tab !== 'archive')) return null;
    // Strict tenant isolation: always filter by companyId (never list global tickets).
    return buildCompanyTicketsQuery(db, {
      companyId,
      archived: archivedView,
      assigneeUid: AGENT_LIKE.includes(role) ? uid : null,
      orderField: 'updatedAt',
      orderDirection: 'desc',
    });
  }, [companyId, uid, role, archivedView, tab]);

  const labelsQuery = useMemo(() => {
    if (!companyId) return null;
    return query(
      collection(db, 'ticketLabels'),
      where('companyId', '==', companyId),
      orderBy('name'),
    );
  }, [companyId]);

  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [labels, setLabels] = useState([]);
  const [labelsLoading, setLabelsLoading] = useState(true);
  const { users: companyUsers } = useCompanyUsers(companyId);
  const { agents: companyAgents } = useCompanyAiAgents(companyId);

  // Per-app (multi-brand) access boundary: only ticketing-enabled apps the user
  // is authorized for. Company admins see all; others see assigned apps only.
  const { apps: ticketingApps, loading: ticketingAppsLoading } = useAgentApps(
    'ticketing',
    userData,
  );
  const authorizedAppIds = useMemo(
    () => ticketingApps.map((a) => a.id),
    [ticketingApps],
  );
  const [activeAppId, setActiveAppId] = useState('all');
  useEffect(() => {
    if (activeAppId !== 'all' && !authorizedAppIds.includes(activeAppId)) {
      setActiveAppId('all');
    }
  }, [authorizedAppIds, activeAppId]);
  const noTicketingApps = !ticketingAppsLoading && ticketingApps.length === 0;

  const sandboxesQuery = useMemo(() => {
    if (!companyId) return null;
    return query(codeSandboxesCollection(db, companyId));
  }, [companyId]);
  const { data: companySandboxes = [] } = useFirestoreQuery(sandboxesQuery, {
    enabled: Boolean(companyId),
  });

  const ticketSandboxMetaById = useMemo(() => {
    const map = {};
    (companySandboxes || []).forEach((row) => {
      const ticketId = typeof row.ticketId === 'string' ? row.ticketId.trim() : '';
      if (!ticketId) return;
      if (!map[ticketId]) {
        map[ticketId] = { total: 0, pending: 0 };
      }
      map[ticketId].total += 1;
      if (
        row.status === 'awaiting_judge_request' ||
        row.status === 'pending_review' ||
        row.status === 'queued_for_qa' ||
        row.status === 'awaiting_human_qa'
      ) {
        map[ticketId].pending += 1;
      }
    });
    return map;
  }, [companySandboxes]);

  useEffect(() => {
    if (!ticketsQuery) {
      setTickets([]);
      setTicketsLoading(false);
      return undefined;
    }

    setTicketsLoading(true);
    setError('');
    const unsubscribe = onSnapshot(
      ticketsQuery,
      (snap) => {
        setTickets(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setTicketsLoading(false);
        setTicketIndexWaiting(false);
        setTicketIndexConsoleUrl(null);
        setError('');
      },
      (err) => {
        console.error('TicketsHub tickets listener:', err);
        if (firestoreIndexStillBuilding(err)) {
          setTicketIndexWaiting(true);
          setTicketIndexConsoleUrl(firestoreIndexUrlFromError(err));
          setTickets([]);
          setError(
            'The tickets search index is still building in Firebase. This is normal right after the first deploy — it often finishes within a few minutes (sometimes longer for large databases). You can create tickets below; the list will load once the index is ready.',
          );
        } else {
          setTicketIndexWaiting(false);
          setTicketIndexConsoleUrl(null);
          setError(err.message || 'Could not load tickets.');
        }
        setTicketsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [ticketsQuery, ticketsRetryKey]);

  useEffect(() => {
    if (!labelsQuery) {
      setLabels([]);
      setLabelsLoading(false);
      return undefined;
    }

    setLabelsLoading(true);
    const unsubscribe = onSnapshot(
      labelsQuery,
      (snap) => {
        setLabels(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLabelsLoading(false);
      },
      (err) => {
        console.error('TicketsHub labels listener:', err);
        setLabelsLoading(false);
      },
    );

    return () => unsubscribe();
  }, [labelsQuery]);

  const loading = ticketsLoading || labelsLoading;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formBody, setFormBody] = useState('');
  const [formStatus, setFormStatus] = useState('open');
  const [formPriority, setFormPriority] = useState('medium');
  const [formLabelIds, setFormLabelIds] = useState([]);
  const [formAssignee, setFormAssignee] = useState(null);
  const [formAiSpendOverrideCalls, setFormAiSpendOverrideCalls] = useState('');
  const [formAiSpendOverrideSpend, setFormAiSpendOverrideSpend] = useState('');
  const [formRejectionCapOverride, setFormRejectionCapOverride] = useState('');
  const [formAppId, setFormAppId] = useState('');
  const [formTargetRepoKey, setFormTargetRepoKey] = useState('');
  const [formLinkedReadRepoKeys, setFormLinkedReadRepoKeys] = useState([]);
  const [gameProjectsWithGithub, setGameProjectsWithGithub] = useState([]);
  const [appProjectsWithGithub, setAppProjectsWithGithub] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const [labelDialog, setLabelDialog] = useState(false);
  const [labelName, setLabelName] = useState('');
  const [labelColor, setLabelColor] = useState('#1976d2');
  const [editingLabel, setEditingLabel] = useState(null);

  const [deleteTicketConfirm, setDeleteTicketConfirm] = useState(null);
  const [deleteLabelConfirm, setDeleteLabelConfirm] = useState(null);
  const [ticketFormSourceAlertTitle, setTicketFormSourceAlertTitle] = useState(null);
  const [linkedTaskSummary, setLinkedTaskSummary] = useState(null);
  const ticketsHubTopRef = useRef(null);

  const loadSuperAdmins = useCallback(async () => {
    if (role !== 'company_admin') {
      setSuperAdminUsers([]);
      return;
    }
    try {
      const q = query(collection(db, 'users'), where('role', '==', 'super_admin'));
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
      rows.sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''));
      setSuperAdminUsers(rows);
    } catch (e) {
      const denied =
        e?.code === 'permission-denied' ||
        /permission.denied/i.test(String(e?.message || ''));
      if (!denied) {
        console.error('loadSuperAdmins:', e);
      }
      setSuperAdminUsers([]);
    }
  }, [role]);

  useEffect(() => {
    if (!ticketIndexWaiting) return undefined;
    const id = window.setInterval(() => {
      setTicketsRetryKey((k) => k + 1);
    }, 25000);
    return () => window.clearInterval(id);
  }, [ticketIndexWaiting]);

  const assigneePool = useMemo(() => {
    const base = [...companyUsers];
    if (role === 'company_admin') {
      superAdminUsers.forEach((sa) => {
        if (!base.some((u) => u.uid === sa.uid)) base.push(sa);
      });
    }
    return base.sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''));
  }, [companyUsers, superAdminUsers, role]);

  const userByUid = useMemo(() => {
    const m = {};
    assigneePool.forEach((u) => {
      m[u.uid] = u;
    });
    return m;
  }, [assigneePool]);

  const agentById = useMemo(() => {
    const m = {};
    companyAgents.forEach((a) => {
      m[a.id] = a;
    });
    return m;
  }, [companyAgents]);

  useEffect(() => {
    loadSuperAdmins();
  }, [loadSuperAdmins]);

  useEffect(() => {
    if (!companyId) {
      setGameProjectsWithGithub([]);
      setAppProjectsWithGithub([]);
      return undefined;
    }
    const unsubGame = onSnapshot(
      collection(db, 'companies', companyId, 'game_projects'),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (p) =>
              typeof p.githubOwner === 'string' &&
              p.githubOwner.trim() &&
              typeof p.githubRepoName === 'string' &&
              p.githubRepoName.trim(),
          )
          .sort((a, b) =>
            String(a.title || '').localeCompare(String(b.title || '')),
          );
        setGameProjectsWithGithub(rows);
      },
      (err) => {
        console.error('game_projects for ticket targetRepo:', err);
        setGameProjectsWithGithub([]);
      },
    );
    const unsubApp = onSnapshot(
      collection(db, 'companies', companyId, 'app_projects'),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (p) =>
              typeof p.githubOwner === 'string' &&
              p.githubOwner.trim() &&
              typeof p.githubRepoName === 'string' &&
              p.githubRepoName.trim(),
          )
          .sort((a, b) =>
            String(a.title || a.name || '').localeCompare(
              String(b.title || b.name || ''),
            ),
          );
        setAppProjectsWithGithub(rows);
      },
      (err) => {
        console.error('app_projects for ticket targetRepo:', err);
        setAppProjectsWithGithub([]);
      },
    );
    return () => {
      unsubGame();
      unsubApp();
    };
  }, [companyId]);

  useEffect(() => {
    if (!newTicketDraft || !companyId || !canWriteTicketing) return;
    const t = typeof newTicketDraft.title === 'string' ? newTicketDraft.title : '';
    const b = typeof newTicketDraft.body === 'string' ? newTicketDraft.body : '';
    const fromAlert =
      typeof newTicketDraft.fromAlertTitle === 'string' && newTicketDraft.fromAlertTitle.trim()
        ? newTicketDraft.fromAlertTitle.trim()
        : '';
    setEditing(null);
    setFormTitle(t.trim().slice(0, 500) || 'Ticket');
    setFormBody(b.slice(0, 8000));
    setFormStatus('open');
    setFormPriority('medium');
    setFormLabelIds([]);
    setFormAssignee(null);
    setFormAiSpendOverrideCalls('');
    setFormAiSpendOverrideSpend('');
    setFormRejectionCapOverride('');
    setFormTargetRepoKey('');
    setTicketFormSourceAlertTitle(fromAlert || null);
    setTab('active');
    setDialogOpen(true);
    onConsumedNewTicketDraft?.();
    requestAnimationFrame(() => {
      ticketsHubTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [newTicketDraft, companyId, canWriteTicketing, onConsumedNewTicketDraft]);

  const closeTicketPeek = useCallback(() => {
    if (saving) return;
    setOpenWorkspaceTickets([]);
    setActiveWorkspaceTab(null);
    setActivePeekTicketId(null);
    setIsTicketMaximized(false);
    setEditing(null);
    setLinkedTaskSummary(null);
    setDescriptionEditing(false);
  }, [saving]);

  const upsertWorkspaceTicket = useCallback((t) => {
    if (!t?.id) return;
    const title =
      typeof t.title === 'string' && t.title.trim() ? t.title.trim() : 'Untitled';
    setOpenWorkspaceTickets((prev) => {
      const existing = prev.find((x) => x.id === t.id);
      if (existing) {
        if (existing.title === title) return prev;
        return prev.map((x) => (x.id === t.id ? { ...x, title } : x));
      }
      return [...prev, { id: t.id, title }];
    });
  }, []);

  const closeTicketDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setTicketFormSourceAlertTitle(null);
    setDescriptionEditing(false);
  };

  const handleViewCodePatches = useCallback(
    (ticketId) => {
      if (!ticketId || saving) return;
      setDialogOpen(false);
      setOpenWorkspaceTickets([]);
      setActiveWorkspaceTab(null);
      setActivePeekTicketId(null);
      setIsTicketMaximized(false);
      setTicketFormSourceAlertTitle(null);
      setLinkedTaskSummary(null);
      setDescriptionEditing(false);
      navigate(dashboardCodeReviewPath(ticketId));
    },
    [navigate, saving],
  );

  useEffect(() => {
    if (!dialogOpen && !activePeekTicketId) return;
    const onTicketsRoute = location.pathname.startsWith(DASHBOARD_TAB_PATHS.Tickets);
    if (!onTicketsRoute) {
      setDialogOpen(false);
      setOpenWorkspaceTickets([]);
      setActiveWorkspaceTab(null);
      setActivePeekTicketId(null);
      setIsTicketMaximized(false);
      setTicketFormSourceAlertTitle(null);
      setLinkedTaskSummary(null);
      setDescriptionEditing(false);
    }
  }, [location.pathname, dialogOpen, activePeekTicketId]);

  useEffect(() => {
    const detailOpen = dialogOpen || Boolean(activePeekTicketId);
    if (!detailOpen || !editing?.linkedTaskId) {
      setLinkedTaskSummary(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const task = await fetchLinkedTaskSummary(db, editing.linkedTaskId);
        if (!cancelled) setLinkedTaskSummary(task);
      } catch (e) {
        console.warn('TicketsHub linked task:', e);
        if (!cancelled) setLinkedTaskSummary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, activePeekTicketId, editing?.linkedTaskId]);

  const openLinkedTaskInKanban = () => {
    if (!editing?.linkedTaskId) return;
    closeTicketPeek();
    navigate(DASHBOARD_TAB_PATHS.Kanban, {
      state: { openTaskId: editing.linkedTaskId },
    });
  };

  const loadTicketIntoForm = useCallback(async (t) => {
    if (!t) return;
    setDescriptionEditing(false);
    setEditing(t);
    setFormTitle(t.title || '');
    setFormBody(t.body || '');
    setFormStatus(t.status || 'open');
    setFormPriority(t.priority || 'medium');
    setFormAppId(typeof t.appId === 'string' ? t.appId : '');
    setFormTargetRepoKey(
      resolveTargetRepoSelectKey(t.targetRepo, {
        gameProjects: gameProjectsWithGithub,
        appProjects: appProjectsWithGithub,
      }),
    );
    setFormLinkedReadRepoKeys(
      resolveLinkedReadRepoSelectKeys(t.linkedReadRepos, {
        gameProjects: gameProjectsWithGithub,
        appProjects: appProjectsWithGithub,
      }),
    );
    setFormLabelIds(Array.isArray(t.labelIds) ? t.labelIds : []);
    let assigneeOption = resolveAssigneeOptionFromTicket(t, assigneePool, agentById);
    if (t.isAssignedToAi && t.assigneeAgentId && !agentById[t.assigneeAgentId]) {
      try {
        const snap = await getDoc(doc(db, 'ai_agents', t.assigneeAgentId));
        if (snap.exists()) {
          assigneeOption = agentToAssigneeOption({ id: snap.id, ...snap.data() });
        }
      } catch (e) {
        console.error('loadTicketIntoForm ai assignee:', e);
      }
    } else if (!assigneeOption && t.assigneeUid) {
      try {
        const snap = await getDoc(doc(db, 'users', t.assigneeUid));
        if (snap.exists()) assigneeOption = humanToAssigneeOption({ uid: snap.id, ...snap.data() });
      } catch (e) {
        console.error('loadTicketIntoForm assignee:', e);
      }
    }
    setFormAssignee(assigneeOption);
  }, [assigneePool, agentById, gameProjectsWithGithub, appProjectsWithGithub]);

  const activateWorkspaceTicket = useCallback(
    async (ticketId) => {
      if (!ticketId) return;
      setActiveWorkspaceTab(ticketId);
      setActivePeekTicketId(ticketId);
      const fromLive = tickets.find((x) => x.id === ticketId);
      if (fromLive) {
        await loadTicketIntoForm(fromLive);
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'tickets', ticketId));
        if (snap.exists()) {
          await loadTicketIntoForm({ id: snap.id, ...snap.data() });
        }
      } catch (e) {
        console.error('activateWorkspaceTicket:', e);
      }
    },
    [tickets, loadTicketIntoForm],
  );

  const closeWorkspaceTab = useCallback(
    (ticketId, event) => {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      if (saving || !ticketId) return;

      setOpenWorkspaceTickets((prev) => {
        const idx = prev.findIndex((x) => x.id === ticketId);
        if (idx < 0) return prev;
        const next = prev.filter((x) => x.id !== ticketId);

        setActiveWorkspaceTab((active) => {
          if (active !== ticketId) return active;
          if (next.length === 0) {
            setActivePeekTicketId(null);
            setIsTicketMaximized(false);
            setEditing(null);
            setLinkedTaskSummary(null);
            setDescriptionEditing(false);
            return null;
          }
          const nextId = next[Math.max(0, idx - 1)].id;
          setActivePeekTicketId(nextId);
          queueMicrotask(() => {
            void activateWorkspaceTicket(nextId);
          });
          return nextId;
        });

        return next;
      });
    },
    [saving, activateWorkspaceTicket],
  );

  const handleWorkspaceTabChange = useCallback(
    async (_event, ticketId) => {
      if (!ticketId || ticketId === activeWorkspaceTab) return;
      setIsTicketMaximized(false);
      await activateWorkspaceTicket(ticketId);
    },
    [activeWorkspaceTab, activateWorkspaceTicket],
  );

  const openCreate = () => {
    closeTicketPeek();
    setEditing(null);
    setFormTitle('');
    setFormBody('');
    setFormStatus('open');
    setFormPriority('medium');
    setFormLabelIds([]);
    setFormAssignee(null);
    setFormAiSpendOverrideCalls('');
    setFormAiSpendOverrideSpend('');
    setFormRejectionCapOverride('');
    setFormTargetRepoKey('');
    setFormLinkedReadRepoKeys([]);
    setFormAppId('');
    setTicketFormSourceAlertTitle(null);
    setDescriptionEditing(true);
    setDialogOpen(true);
  };

  const openEdit = async (t) => {
    if (!t?.id) return;
    upsertWorkspaceTicket(t);
    setDialogOpen(false);
    setIsTicketMaximized(false);
    if (activeWorkspaceTab === t.id && activePeekTicketId === t.id) {
      return;
    }
    setActiveWorkspaceTab(t.id);
    setActivePeekTicketId(t.id);
    await loadTicketIntoForm(t);
  };

  const labelMap = useMemo(() => {
    const m = {};
    labels.forEach((l) => { m[l.id] = l; });
    return m;
  }, [labels]);

  const assigneeAutocompleteOptions = useMemo(
    () => buildAssigneeAutocompleteOptions(assigneePool, companyAgents, formAssignee),
    [assigneePool, companyAgents, formAssignee],
  );

  const assigneeTableLabel = (t) => ticketAssigneeDisplayLabel(t, userByUid, agentById);

  const displayedTickets = useMemo(() => {
    let list = tickets;
    if (!showResolved) {
      list = list.filter((t) => t.status !== 'resolved');
    }
    if (showAiActivityOnly) {
      list = list.filter(
        (t) =>
          t.source === 'ai_agent_delegation' ||
          t.isAssignedToAi === true ||
          Boolean(t.delegatedByAgentId),
      );
    }
    // Per-app isolation: authorized apps plus global (unscoped) tickets.
    const appIdSet = new Set(authorizedAppIds);
    if (activeAppId === 'all') {
      list = list.filter((t) => !t.appId || appIdSet.has(t.appId));
    } else {
      list = list.filter((t) => t.appId === activeAppId);
    }
    return list;
  }, [tickets, showResolved, showAiActivityOnly, activeAppId, authorizedAppIds]);

  const selectableTickets = useMemo(
    () => displayedTickets.filter((t) => canModifyTicket(t)),
    [displayedTickets, role, uid, canWriteTicketing],
  );

  const selectedCount = useMemo(
    () => Object.keys(selectedIds).filter((id) => selectedIds[id]).length,
    [selectedIds],
  );

  const allSelectableSelected =
    selectableTickets.length > 0 &&
    selectableTickets.every((t) => selectedIds[t.id]);

  const someSelectableSelected =
    selectableTickets.some((t) => selectedIds[t.id]) && !allSelectableSelected;

  const tableColSpan = useMemo(() => {
    const base = canWriteTicketing ? 8 : 6;
    const peekOpen =
      Boolean(activePeekTicketId) &&
      !isTicketMaximized &&
      openWorkspaceTickets.length > 0;
    return peekOpen ? base - 2 : base;
  }, [canWriteTicketing, activePeekTicketId, isTicketMaximized, openWorkspaceTickets.length]);

  const splitPeekOpen =
    Boolean(activePeekTicketId) &&
    !isTicketMaximized &&
    openWorkspaceTickets.length > 0;

  useEffect(() => {
    setSelectedIds({});
  }, [tab, showResolved, showAiActivityOnly]);

  useEffect(() => {
    if (tab !== 'active' && tab !== 'archive' && activePeekTicketId) {
      setOpenWorkspaceTickets([]);
      setActiveWorkspaceTab(null);
      setActivePeekTicketId(null);
      setIsTicketMaximized(false);
      setEditing(null);
      setDescriptionEditing(false);
    }
  }, [tab, activePeekTicketId]);

  const toggleSelectAll = () => {
    if (allSelectableSelected) {
      setSelectedIds({});
      return;
    }
    const next = {};
    selectableTickets.forEach((t) => {
      next[t.id] = true;
    });
    setSelectedIds(next);
  };

  const toggleSelectOne = (ticketId) => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      if (next[ticketId]) delete next[ticketId];
      else next[ticketId] = true;
      return next;
    });
  };

  const applyBulkStatus = async () => {
    if (!bulkStatus || selectedCount === 0 || !companyId || !uid || !canWriteTicketing) return;
    const ids = Object.keys(selectedIds).filter((id) => selectedIds[id]);
    const ticketsToUpdate = displayedTickets.filter(
      (t) => ids.includes(t.id) && canModifyTicket(t) && t.status !== bulkStatus,
    );
    if (ticketsToUpdate.length === 0) {
      setError('No selected tickets can be updated to that status.');
      return;
    }

    setBulkUpdating(true);
    setError('');
    try {
      await Promise.all(
        ticketsToUpdate.map(async (ticket) => {
          await updateDoc(doc(db, 'tickets', ticket.id), {
            status: bulkStatus,
            ...buildClearAiAssignmentIfTerminal(bulkStatus, { forUpdate: true }),
            updatedAt: serverTimestamp(),
            updatedBy: uid,
          });
          const next = {
            ...ticket,
            status: bulkStatus,
            ...(bulkStatus === 'resolved' || bulkStatus === 'closed'
              ? { isAssignedToAi: false, assigneeAgentId: null }
              : {}),
          };
          await appendTicketLog(db, {
            companyId,
            ticketId: ticket.id,
            action: 'ticket_updated',
            summary: `Updated: status → ${statusLabel(bulkStatus)}`,
            meta: { changes: [`status → ${statusLabel(bulkStatus)}`], bulk: true },
            actorUid: uid,
            actorEmail: email,
            actorRole: role,
            participantUids: participantsForTicket(next, uid),
          });
        }),
      );
      setSelectedIds({});
      setBulkStatus('');
    } catch (e) {
      console.error('applyBulkStatus:', e);
      setError(e.message || 'Could not update selected tickets.');
    } finally {
      setBulkUpdating(false);
    }
  };

  useEffect(() => {
    if (!openTicketId || !companyId || !uid) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'tickets', openTicketId));
        if (cancelled) return;
        if (!snap.exists()) {
          onConsumedOpenTicket?.();
          return;
        }
        const d = { id: snap.id, ...snap.data() };
        if (d.companyId !== companyId) {
          onConsumedOpenTicket?.();
          return;
        }
        setTab(d.archived ? 'archive' : 'active');
        await openEdit(d);
      } catch (e) {
        console.error('openTicketId:', e);
      } finally {
        if (!cancelled) onConsumedOpenTicket?.();
      }
    })();

    return () => {
      cancelled = true;
    };
  // openEdit is stable enough for this deep-link flow
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTicketId, companyId, uid, role]);

  const saveTicket = async () => {
    if (!companyId || !uid || !formTitle.trim() || !canWriteTicketing) return;
    if (editing && !canModifyTicket(editing)) return;
    if (forceTicketAppSelection && !formAppId) {
      setError('Please select an App/Site for this ticket.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const reporterUid = editing?.reporterUid || uid;
      const assignment = buildTicketAssignmentPayload(formAssignee, reporterUid, {
        forUpdate: Boolean(editing),
      });
      const prevAssigneeKey = editing ? ticketAssigneeKey(editing) : null;
      const nextAssigneeKey = assigneeOptionKey(formAssignee);
      const assigneeUnchanged = Boolean(editing && prevAssigneeKey === nextAssigneeKey);
      if (
        formAssignee?.kind !== ASSIGNEE_KIND_AI
        && formAssignee?.role === 'super_admin'
        && formAssignee?.uid
        && role !== 'company_admin'
        && role !== 'super_admin'
        && !assigneeUnchanged
      ) {
        setError('Only a company admin can assign tickets to a Super Admin.');
        setSaving(false);
        return;
      }
      const labelIds = formLabelIds.filter(Boolean);
      const bodyFields = buildTicketBodyPayload(formBody, userData);
      const targetRepoFields = buildTicketTargetRepoPayload(
        formTargetRepoKey,
        {
          gameProjects: gameProjectsWithGithub,
          appProjects: appProjectsWithGithub,
        },
      );
      const linkedReadFields = buildLinkedReadReposPayload(
        formLinkedReadRepoKeys,
        {
          gameProjects: gameProjectsWithGithub,
          appProjects: appProjectsWithGithub,
        },
        { primarySelectKey: formTargetRepoKey },
      );
      const terminalAiClear = buildClearAiAssignmentIfTerminal(formStatus, {
        forUpdate: Boolean(editing),
      });
      if (editing) {
        const prev = { ...editing };
        const bodyChanged = prev.body !== bodyFields.body;
        await updateDoc(doc(db, 'tickets', editing.id), {
          title: formTitle.trim(),
          ...bodyFields,
          status: formStatus,
          priority: formPriority,
          appId: formAppId || null,
          labelIds,
          ...assignment,
          ...terminalAiClear,
          ...targetRepoFields,
          ...linkedReadFields,
          updatedAt: serverTimestamp(),
          updatedBy: uid,
          ...(bodyChanged ? { bodyUpdatedAt: serverTimestamp() } : {}),
        });
        const next = {
          ...prev,
          title: formTitle.trim(),
          ...bodyFields,
          status: formStatus,
          priority: formPriority,
          labelIds,
          assigneeUid: assignment.assigneeUid ?? null,
          assigneeAgentId:
            terminalAiClear.isAssignedToAi === false
              ? null
              : formAssignee?.kind === ASSIGNEE_KIND_AI
                ? formAssignee.agentId
                : null,
          isAssignedToAi:
            terminalAiClear.isAssignedToAi === false
              ? false
              : assignment.isAssignedToAi === true,
          platformEscalation: assignment.platformEscalation === true,
          reporterUid: prev.reporterUid,
          ...targetRepoFields,
          ...linkedReadFields,
        };
        const parts = participantsForTicket(next, uid);
        const changes = [];
        if (prev.title !== next.title) changes.push(`title`);
        if (prev.body !== next.body) changes.push('description');
        if (prev.status !== next.status) changes.push(`status → ${statusLabel(next.status)}`);
        if (prev.priority !== next.priority) changes.push(`priority → ${priorityLabel(next.priority)}`);
        if (ticketAssigneeKey(prev) !== ticketAssigneeKey(next)) changes.push('assignee');
        if (JSON.stringify(prev.labelIds || []) !== JSON.stringify(next.labelIds || [])) changes.push('labels');
        await appendTicketLog(db, {
          companyId,
          ticketId: editing.id,
          action: 'ticket_updated',
          summary: changes.length ? `Updated: ${changes.join(', ')}` : 'Ticket updated',
          meta: { changes },
          actorUid: uid,
          actorEmail: email,
          actorRole: role,
          participantUids: parts,
        });
        try {
          await appendTicketInboxAlert(db, {
            companyId,
            ticketId: editing.id,
            title: `Ticket updated: ${next.title || 'Ticket'}`,
            message: changes.length ? `Changes: ${changes.join(', ')}` : 'Ticket was updated.',
            severity: 'info',
            ruleCode: 'TICKET_UPDATED',
            ticketAction: 'updated',
            notifySuperAdmins: Boolean(next.platformEscalation),
            actorUid: uid,
            actorEmail: email,
          });
        } catch (e) {
          console.warn('appendTicketInboxAlert:', e);
        }
        setEditing((prev) => (prev ? {
          ...prev,
          title: formTitle.trim(),
          ...bodyFields,
          status: formStatus,
          priority: formPriority,
          appId: formAppId || null,
          labelIds,
          ...targetRepoFields,
          ...linkedReadFields,
          bodyUpdatedAt: bodyChanged ? new Date() : prev.bodyUpdatedAt,
        } : prev));
        upsertWorkspaceTicket({ id: editing.id, title: formTitle.trim() });
        setDescriptionEditing(false);
      } else {
        const callsRaw = formAiSpendOverrideCalls.trim();
        const spendRaw = formAiSpendOverrideSpend.trim();
        const rejectionRaw = formRejectionCapOverride.trim();
        const callsN = callsRaw === '' ? null : Number(callsRaw);
        const spendN = spendRaw === '' ? null : Number(spendRaw);
        if (callsRaw !== '' && (!Number.isFinite(callsN) || callsN <= 0)) {
          setError('AI spend override max calls must be a positive number (or blank).');
          setSaving(false);
          return;
        }
        if (spendRaw !== '' && (!Number.isFinite(spendN) || spendN <= 0)) {
          setError('AI spend override max $ must be a positive number (or blank).');
          setSaving(false);
          return;
        }
        let rejectionMax = null;
        if (rejectionRaw !== '') {
          const n = Number(rejectionRaw);
          const floored = Number.isFinite(n) ? Math.floor(n) : NaN;
          if (!Number.isFinite(floored) || floored < 1 || floored > 20) {
            setError('Rejection override must be an integer from 1 to 20 (or blank).');
            setSaving(false);
            return;
          }
          rejectionMax = floored;
        }
        const aiSpendCapOverrideFields =
          callsN == null && spendN == null
            ? {}
            : {
                aiSpendCapOverride: {
                  ...(callsN != null ? { maxCallCount: Math.floor(callsN) } : {}),
                  ...(spendN != null ? { maxSpendUsd: spendN } : {}),
                },
              };
        const rejectionCapOverrideFields =
          rejectionMax == null
            ? {}
            : { rejectionCapOverride: { maxCount: rejectionMax } };
        const ref = await addDoc(collection(db, 'tickets'), {
          companyId,
          appId: formAppId || null,
          title: formTitle.trim(),
          ...bodyFields,
          status: formStatus,
          priority: formPriority,
          labelIds,
          ...assignment,
          ...terminalAiClear,
          ...targetRepoFields,
          ...linkedReadFields,
          ...aiSpendCapOverrideFields,
          ...rejectionCapOverrideFields,
          archived: false,
          archivedAt: null,
          reporterUid: uid,
          aiEnriched: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: uid,
          createdByEmail: email || null,
          updatedBy: uid,
        });
        const t = {
          id: ref.id,
          reporterUid: uid,
          assigneeUid: assignment.assigneeUid ?? null,
          assigneeAgentId:
            terminalAiClear.isAssignedToAi === false
              ? null
              : formAssignee?.kind === ASSIGNEE_KIND_AI
                ? formAssignee.agentId
                : null,
          isAssignedToAi:
            terminalAiClear.isAssignedToAi === false
              ? false
              : assignment.isAssignedToAi === true,
        };
        await appendTicketLog(db, {
          companyId,
          ticketId: ref.id,
          action: 'ticket_created',
          summary: `Ticket created: ${formTitle.trim()}`,
          meta: { title: formTitle.trim() },
          actorUid: uid,
          actorEmail: email,
          actorRole: role,
          participantUids: participantsForTicket(t, uid),
        });
        try {
          await appendTicketInboxAlert(db, {
            companyId,
            ticketId: ref.id,
            title: `New ticket: ${formTitle.trim()}`,
            message: `Created by ${email}.`,
            severity: 'info',
            ruleCode: 'TICKET_CREATED',
            ticketAction: 'created',
            notifySuperAdmins: assignment.platformEscalation === true,
            actorUid: uid,
            actorEmail: email,
          });
        } catch (e) {
          console.warn('appendTicketInboxAlert:', e);
        }
        setDescriptionEditing(false);
        setDialogOpen(false);
        const created = {
          id: ref.id,
          companyId,
          appId: formAppId || null,
          title: formTitle.trim(),
          ...bodyFields,
          status: formStatus,
          priority: formPriority,
          labelIds,
          reporterUid: uid,
          assigneeUid: assignment.assigneeUid ?? null,
          assigneeAgentId:
            formAssignee?.kind === ASSIGNEE_KIND_AI ? formAssignee.agentId : null,
          isAssignedToAi: assignment.isAssignedToAi === true,
          platformEscalation: assignment.platformEscalation === true,
          archived: false,
        };
        upsertWorkspaceTicket(created);
        setActiveWorkspaceTab(ref.id);
        setActivePeekTicketId(ref.id);
        setIsTicketMaximized(false);
        setEditing(created);
      }
      setTicketFormSourceAlertTitle(null);
    } catch (e) {
      console.error('saveTicket:', e);
      setError(e.message || 'Could not save ticket.');
    } finally {
      setSaving(false);
    }
  };

  const setArchived = async (ticket, archived) => {
    if (!canModifyTicket(ticket)) return;
    try {
      await updateDoc(doc(db, 'tickets', ticket.id), {
        archived,
        archivedAt: archived ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
        updatedBy: uid,
      });
      const t = { ...ticket, archived, assigneeUid: ticket.assigneeUid, reporterUid: ticket.reporterUid };
      await appendTicketLog(db, {
        companyId,
        ticketId: ticket.id,
        action: archived ? 'ticket_archived' : 'ticket_restored',
        summary: archived ? `Archived ticket: ${ticket.title}` : `Restored ticket: ${ticket.title}`,
        actorUid: uid,
        actorEmail: email,
        actorRole: role,
        participantUids: participantsForTicket(t, uid),
      });
      try {
        await appendTicketInboxAlert(db, {
          companyId,
          ticketId: ticket.id,
          title: archived ? `Ticket archived: ${ticket.title}` : `Ticket restored: ${ticket.title}`,
          message: archived ? 'Moved to archive.' : 'Returned to active list.',
          severity: 'info',
          ruleCode: archived ? 'TICKET_ARCHIVED' : 'TICKET_RESTORED',
          ticketAction: archived ? 'archived' : 'restored',
          notifySuperAdmins: Boolean(t.platformEscalation),
          actorUid: uid,
          actorEmail: email,
        });
      } catch (e) {
        console.warn('appendTicketInboxAlert:', e);
      }
    } catch (e) {
      console.error('archive:', e);
      setError(e.message || 'Could not update archive state.');
    }
  };

  const confirmDeleteTicket = async () => {
    const ticket = deleteTicketConfirm;
    if (!ticket || !canRemoveTicket(ticket)) return;
    try {
      await appendTicketLog(db, {
        companyId,
        ticketId: ticket.id,
        action: 'ticket_deleted',
        summary: `Ticket deleted: ${ticket.title}`,
        meta: { formerId: ticket.id },
        actorUid: uid,
        actorEmail: email,
        actorRole: role,
        participantUids: participantsForTicket(ticket, uid),
      });
      try {
        await appendTicketInboxAlert(db, {
          companyId,
          ticketId: ticket.id,
          title: `Ticket deleted: ${ticket.title}`,
          message: 'This ticket was permanently removed.',
          severity: 'warning',
          ruleCode: 'TICKET_DELETED',
          ticketAction: 'deleted',
          notifySuperAdmins: Boolean(ticket.platformEscalation),
          actorUid: uid,
          actorEmail: email,
        });
      } catch (e) {
        console.warn('appendTicketInboxAlert:', e);
      }
      await deleteDoc(doc(db, 'tickets', ticket.id));
      setDeleteTicketConfirm(null);
      closeWorkspaceTab(ticket.id);
    } catch (e) {
      console.error('delete ticket:', e);
      setError(e.message || 'Could not delete ticket.');
    }
  };

  const saveLabel = async () => {
    if (!companyId || !uid || !labelName.trim() || !canManageLabels) return;
    try {
      if (editingLabel) {
        await updateDoc(doc(db, 'ticketLabels', editingLabel.id), {
          name: labelName.trim(),
          color: labelColor,
          updatedAt: serverTimestamp(),
        });
        await appendTicketLog(db, {
          companyId,
          ticketId: null,
          action: 'label_updated',
          summary: `Label updated: ${labelName.trim()}`,
          actorUid: uid,
          actorEmail: email,
          actorRole: role,
          participantUids: [uid],
        });
      } else {
        await addDoc(collection(db, 'ticketLabels'), {
          companyId,
          name: labelName.trim(),
          color: labelColor,
          createdAt: serverTimestamp(),
          createdBy: uid,
        });
        await appendTicketLog(db, {
          companyId,
          ticketId: null,
          action: 'label_created',
          summary: `Label created: ${labelName.trim()}`,
          actorUid: uid,
          actorEmail: email,
          actorRole: role,
          participantUids: [uid],
        });
      }
      setLabelDialog(false);
      setEditingLabel(null);
      setLabelName('');
    } catch (e) {
      console.error('saveLabel:', e);
      setError(e.message || 'Could not save label.');
    }
  };

  const confirmDeleteLabel = async () => {
    const lbl = deleteLabelConfirm;
    if (!lbl || !canManageLabels) return;
    try {
      await appendTicketLog(db, {
        companyId,
        ticketId: null,
        action: 'label_deleted',
        summary: `Label deleted: ${lbl.name}`,
        actorUid: uid,
        actorEmail: email,
        actorRole: role,
        participantUids: [uid],
      });
      await deleteDoc(doc(db, 'ticketLabels', lbl.id));
      setDeleteLabelConfirm(null);
    } catch (e) {
      console.error('delete label:', e);
      setError(e.message || 'Could not delete label.');
    }
  };

  if (!companyId) {
    return <Alert severity="info">You need a company assignment to use tickets.</Alert>;
  }

  const ticketFormReadOnly = Boolean(editing && !canModifyTicket(editing));

  const ticketDetailViewProps = {
    ticketId: activePeekTicketId,
    onClose: () => {
      if (activeWorkspaceTab) closeWorkspaceTab(activeWorkspaceTab);
      else closeTicketPeek();
    },
    editing,
    ticketFormReadOnly,
    formTitle,
    onFormTitleChange: setFormTitle,
    formBody,
    onFormBodyChange: setFormBody,
    formStatus,
    onFormStatusChange: setFormStatus,
    formPriority,
    onFormPriorityChange: setFormPriority,
    formAppId,
    onFormAppIdChange: setFormAppId,
    ticketApps: ticketingApps,
    forceAppSelection: forceTicketAppSelection,
    formTargetRepoKey,
    onFormTargetRepoKeyChange: setFormTargetRepoKey,
    formLinkedReadRepoKeys,
    onFormLinkedReadRepoKeysChange: setFormLinkedReadRepoKeys,
    gameProjectsWithGithub,
    appProjectsWithGithub,
    projectAiSpendCap: (() => {
      const parsed = parseTargetRepoSelectKey(formTargetRepoKey);
      if (parsed?.kind === PROJECT_KIND_GAME) {
        return (
          gameProjectsWithGithub.find((p) => p.id === parsed.projectId)
            ?.aiSpendCap || null
        );
      }
      if (editing?.targetRepo?.gameProjectId) {
        return (
          gameProjectsWithGithub.find(
            (p) => p.id === editing.targetRepo.gameProjectId,
          )?.aiSpendCap || null
        );
      }
      return null;
    })(),
    workspaceAiSpendCap: workspace?.aiSpendCap || null,
    workspaceMaxRejectionCount: workspace?.maxRejectionCount ?? null,
    formLabelIds,
    onFormLabelIdsChange: setFormLabelIds,
    formAssignee,
    onFormAssigneeChange: setFormAssignee,
    saving,
    onSave: saveTicket,
    labels,
    assigneeAutocompleteOptions,
    descriptionEditing,
    onDescriptionEditingChange: setDescriptionEditing,
    linkedTaskSummary,
    onOpenLinkedTaskInKanban: openLinkedTaskInKanban,
    ticketFormSourceAlertTitle,
    ticketSandboxMetaById,
    onViewCodePatches: handleViewCodePatches,
    canModifyTicket,
    companyId,
    uid,
    email,
    role,
    userData,
    userByUid,
    agentById,
    formatDateTime,
    timeZoneLabel,
  };

  const peekPanelHeader = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        flexShrink: 0,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        minHeight: 40,
      }}
    >
      <Tabs
        value={activeWorkspaceTab || false}
        onChange={handleWorkspaceTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 40,
          '& .MuiTabs-indicator': { height: 2 },
          '& .MuiTab-root': {
            minHeight: 40,
            py: 0,
            px: 1,
            textTransform: 'none',
            maxWidth: 220,
          },
        }}
      >
        {openWorkspaceTickets.map((wt) => (
          <Tab
            key={wt.id}
            value={wt.id}
            label={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  maxWidth: 200,
                  minWidth: 0,
                }}
              >
                <Typography
                  component="span"
                  variant="caption"
                  fontWeight={600}
                  noWrap
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  {wt.title || 'Untitled'}
                </Typography>
                <IconButton
                  component="span"
                  size="small"
                  aria-label={`Close ${wt.title || 'ticket'} tab`}
                  title="Close tab"
                  onClick={(e) => closeWorkspaceTab(wt.id, e)}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  sx={{
                    p: 0.25,
                    ml: 0.25,
                    color: 'text.secondary',
                    '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            }
          />
        ))}
      </Tabs>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          px: 0.75,
          flexShrink: 0,
          borderLeft: 1,
          borderColor: 'divider',
        }}
      >
        <IconButton
          size="small"
          onClick={() => setIsTicketMaximized(true)}
          title="Maximize"
          aria-label="Maximize ticket"
        >
          <OpenInFullIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box
      ref={ticketsHubTopRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        width: '100%',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          px: 1.5,
          py: 0.75,
          minHeight: 44,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.5,
              textTransform: 'none',
              fontWeight: 600,
            },
          }}
        >
          <Tab label="Active" value="active" />
          <Tab label="Archive" value="archive" icon={<ArchiveOutlinedIcon />} iconPosition="start" />
          {canManageLabels && (
            <Tab label="Labels" value="labels" icon={<LabelIcon />} iconPosition="start" />
          )}
        </Tabs>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {ticketingApps.length > 0 && (
            <TextField
              select
              size="small"
              label="Active app"
              value={activeAppId}
              onChange={(e) => setActiveAppId(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="all">All my apps</MenuItem>
              {ticketingApps.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.appName}
                </MenuItem>
              ))}
            </TextField>
          )}
          {(tab === 'active' || tab === 'archive') && (
            <>
              <Chip
                size="small"
                label={showResolved ? 'Hide resolved' : 'Show resolved'}
                color={showResolved ? 'primary' : 'default'}
                variant={showResolved ? 'filled' : 'outlined'}
                onClick={() => setShowResolved((prev) => !prev)}
                sx={{ cursor: 'pointer' }}
              />
              <Chip
                size="small"
                label={showAiActivityOnly ? 'AI only' : 'All tickets'}
                color={showAiActivityOnly ? 'secondary' : 'default'}
                variant={showAiActivityOnly ? 'filled' : 'outlined'}
                icon={<SmartToyOutlinedIcon />}
                onClick={() => setShowAiActivityOnly((prev) => !prev)}
                sx={{ cursor: 'pointer' }}
              />
            </>
          )}
          {canWriteTicketing && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={openCreate}
              disabled={noTicketingApps && forceTicketAppSelection}
              sx={{ fontWeight: 700 }}
            >
              New ticket
            </Button>
          )}
        </Stack>
      </Box>

      {error && (
        <Alert
          severity={ticketIndexWaiting ? 'info' : 'error'}
          sx={{ mx: 1.5, mt: 1, flexShrink: 0, py: 0.5 }}
          onClose={() => {
            setError('');
            setTicketIndexWaiting(false);
            setTicketIndexConsoleUrl(null);
          }}
        >
          <Typography variant="body2">{error}</Typography>
          {ticketIndexConsoleUrl && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <Link href={ticketIndexConsoleUrl} target="_blank" rel="noopener noreferrer">
                Open index status in Firebase console
              </Link>
            </Typography>
          )}
          {ticketIndexWaiting && (
            <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => setTicketsRetryKey((k) => k + 1)}>
              Retry now
            </Button>
          )}
        </Alert>
      )}

      {canWriteTicketing && selectedCount > 0 && (tab === 'active' || tab === 'archive') && (
        <Box sx={{ px: 1.5, py: 0.75, flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
          <TicketBulkActions
            selectedCount={selectedCount}
            bulkStatus={bulkStatus}
            bulkUpdating={bulkUpdating}
            onBulkStatusChange={setBulkStatus}
            onApplyBulkStatus={applyBulkStatus}
            onClearSelection={() => setSelectedIds({})}
          />
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {(tab === 'active' || tab === 'archive') && noTicketingApps && forceTicketAppSelection && (
          <Alert severity="info" sx={{ m: 1.5 }}>
            You&apos;re not assigned to any ticketing-enabled apps yet, and this workspace requires
            an App/Site on every ticket. Ask a company admin to assign you to an app in{' '}
            <strong>Product Portfolio</strong>.
          </Alert>
        )}

        {(tab === 'active' || tab === 'archive') && !(noTicketingApps && forceTicketAppSelection) && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                flex: splitPeekOpen ? '0 0 60%' : '1 1 auto',
                minWidth: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                transition: 'flex-basis 0.25s ease',
              }}
            >
              <TicketTable
                canWriteTicketing={canWriteTicketing}
                loading={loading}
                displayedTickets={displayedTickets}
                tableColSpan={tableColSpan}
                canModifyTicket={canModifyTicket}
                canRemoveTicket={canRemoveTicket}
                selectedIds={selectedIds}
                bulkUpdating={bulkUpdating}
                someSelectableSelected={someSelectableSelected}
                allSelectableSelected={allSelectableSelected}
                selectableTicketsCount={selectableTickets.length}
                onToggleSelectAll={toggleSelectAll}
                onToggleSelectOne={toggleSelectOne}
                onOpenEdit={openEdit}
                activeTicketId={activePeekTicketId}
                compact={splitPeekOpen}
                labelMap={labelMap}
                userByUid={userByUid}
                agentById={agentById}
                formatDateTime={formatDateTime}
                ticketSandboxMetaById={ticketSandboxMetaById}
                onViewCodePatches={handleViewCodePatches}
                assigneeTableLabel={assigneeTableLabel}
                showAiActivityOnly={showAiActivityOnly}
                showResolved={showResolved}
                archivedView={archivedView}
                onSetArchived={setArchived}
                onDeleteTicketConfirm={setDeleteTicketConfirm}
              />
            </Box>

            {splitPeekOpen && (
              <Box
                sx={{
                  flex: '0 0 40%',
                  minWidth: 280,
                  maxWidth: '40%',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: 1,
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  transition: 'opacity 0.25s ease, transform 0.25s ease',
                }}
              >
                {peekPanelHeader}
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <TicketDetailView fillHeight {...ticketDetailViewProps} />
                </Box>
              </Box>
            )}
          </Box>
        )}

        {tab === 'labels' && canManageLabels && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1.5 }}>
            <Stack spacing={2}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingLabel(null);
                  setLabelName('');
                  setLabelColor('#1976d2');
                  setLabelDialog(true);
                }}
                sx={{ alignSelf: 'flex-start' }}
              >
                New label
              </Button>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Color</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {labels.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">
                          No labels yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {labels.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>
                        <Chip label={l.name} size="small" sx={{ bgcolor: l.color, color: '#fff' }} />
                      </TableCell>
                      <TableCell>{l.color}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() => {
                            setEditingLabel(l);
                            setLabelName(l.name);
                            setLabelColor(l.color || '#1976d2');
                            setLabelDialog(true);
                          }}
                        >
                          Edit
                        </Button>
                        <Button size="small" color="error" onClick={() => setDeleteLabelConfirm(l)}>
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Stack>
          </Box>
        )}
      </Box>

      <TicketDetailModal
        open={dialogOpen}
        onClose={closeTicketDialog}
        ticketFormReadOnly={false}
        formTitle={formTitle}
        onFormTitleChange={setFormTitle}
        formBody={formBody}
        onFormBodyChange={setFormBody}
        formStatus={formStatus}
        onFormStatusChange={setFormStatus}
        formPriority={formPriority}
        onFormPriorityChange={setFormPriority}
        formAppId={formAppId}
        onFormAppIdChange={setFormAppId}
        ticketApps={ticketingApps}
        forceAppSelection={forceTicketAppSelection}
        formTargetRepoKey={formTargetRepoKey}
        onFormTargetRepoKeyChange={setFormTargetRepoKey}
        formLinkedReadRepoKeys={formLinkedReadRepoKeys}
        onFormLinkedReadRepoKeysChange={setFormLinkedReadRepoKeys}
        gameProjectsWithGithub={gameProjectsWithGithub}
        appProjectsWithGithub={appProjectsWithGithub}
        formLabelIds={formLabelIds}
        onFormLabelIdsChange={setFormLabelIds}
        formAssignee={formAssignee}
        onFormAssigneeChange={setFormAssignee}
        formAiSpendOverrideCalls={formAiSpendOverrideCalls}
        onFormAiSpendOverrideCalls={setFormAiSpendOverrideCalls}
        formAiSpendOverrideSpend={formAiSpendOverrideSpend}
        onFormAiSpendOverrideSpend={setFormAiSpendOverrideSpend}
        formRejectionCapOverride={formRejectionCapOverride}
        onFormRejectionCapOverride={setFormRejectionCapOverride}
        saving={saving}
        onSave={saveTicket}
        labels={labels}
        assigneeAutocompleteOptions={assigneeAutocompleteOptions}
        descriptionEditing={descriptionEditing}
        onDescriptionEditingChange={setDescriptionEditing}
        ticketFormSourceAlertTitle={ticketFormSourceAlertTitle}
        canModifyTicket={canModifyTicket}
        companyId={companyId}
        uid={uid}
        email={email}
        role={role}
        userData={userData}
        userByUid={userByUid}
        agentById={agentById}
        formatDateTime={formatDateTime}
        timeZoneLabel={timeZoneLabel}
      />

      <Dialog
        fullScreen
        open={isTicketMaximized && Boolean(activePeekTicketId)}
        onClose={() => setIsTicketMaximized(false)}
        TransitionComponent={Fade}
        transitionDuration={250}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 2,
              py: 1,
              minHeight: 52,
              flexShrink: 0,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
              {formTitle || 'Ticket'}
            </Typography>
            <IconButton
              onClick={() => setIsTicketMaximized(false)}
              title="Minimize to side panel"
              aria-label="Minimize ticket"
            >
              <CloseFullscreenIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                if (activeWorkspaceTab) closeWorkspaceTab(activeWorkspaceTab);
                else closeTicketPeek();
              }}
              title="Close"
              aria-label="Close ticket"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <TicketDetailView fillHeight {...ticketDetailViewProps} />
          </Box>
        </Box>
      </Dialog>

      <Dialog open={labelDialog} onClose={() => setLabelDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editingLabel ? 'Edit label' : 'New label'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={labelName} onChange={(e) => setLabelName(e.target.value)} fullWidth />
            <TextField label="Color" type="color" value={labelColor} onChange={(e) => setLabelColor(e.target.value)} sx={{ width: 120 }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveLabel} disabled={!labelName.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTicketConfirm)} onClose={() => setDeleteTicketConfirm(null)}>
        <DialogTitle>Delete ticket?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This permanently removes &quot;{deleteTicketConfirm?.title}&quot;. A deletion record is kept in the activity log.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTicketConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteTicket}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteLabelConfirm)} onClose={() => setDeleteLabelConfirm(null)}>
        <DialogTitle>Delete label?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Remove &quot;{deleteLabelConfirm?.name}&quot;? Tickets still referencing this label id may show a raw id until edited.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteLabelConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteLabel}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
